// src/components/ConfigurationSection.jsx
import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Divider,
  Paper,
  Grid,
  Stack,
  TableContainer,
  Table,
  TableRow,
  TableCell,
  TableHead,
  TableBody,
  Button,
  TextField,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  ImageList,
  ImageListItem,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import PlaylistAddCheckIcon from "@mui/icons-material/PlaylistAddCheck";

import {
  fetchPrices,
  savePrices,
  fetchRoomImages,
  uploadRoomImage,
  deleteRoomImage,
  fetchRoomAmenities,
  saveRoomAmenities,
  fetchBillingCurrency,
  saveBillingCurrency,
} from "../api/dashboard";

function TabPanel({ children, value, index }) {
  return value === index ? <Box sx={{ mt: 3 }}>{children}</Box> : null;
}

const Info = ({ label, value }) => (
  <Grid item xs={12} sm={6}>
    <Typography variant="body2" color="textSecondary">
      {label}
    </Typography>
    <Typography variant="body1" fontWeight="bold">
      {value !== undefined && value !== null ? value.toString() : "—"}
    </Typography>
  </Grid>
);

export default function ConfigurationSection({ user }) {
  // ✅ NO early return antes de hooks
  const [tab, setTab] = useState(0);

  // ===== PRICES (1 por roomId) =====
  const [roomPrices, setRoomPrices] = useState({});
  const [editedPrices, setEditedPrices] = useState({});
  const [loadingPrices, setLoadingPrices] = useState(false);

  const [startDate, setStartDate] = useState(() => {
    const today = new Date().toISOString().split("T")[0];
    return today;
  });
  const [endDate, setEndDate] = useState(() => {
    const nextWeek = new Date();
    nextWeek.setDate(nextWeek.getDate() + 7);
    return nextWeek.toISOString().split("T")[0];
  });

  // ===== BILLING CURRENCY =====
  const [billingCurrency, setBillingCurrency] = useState(
    user?.billing?.currency || "ARS",
  );

  // ===== IMAGES (por unidad) =====
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [activeRoomForImages, setActiveRoomForImages] = useState(null);

  // guardamos por unidad: { "roomId:unitId": [imgs] }
  const [imagesByUnit, setImagesByUnit] = useState({});
  const [imagesLoadingUnitKey, setImagesLoadingUnitKey] = useState(null);

  // ===== AMENITIES (✅ AHORA: por unidad, igual que imágenes) =====
  const [amenitiesDialogOpen, setAmenitiesDialogOpen] = useState(false);
  const [activeRoomForAmenities, setActiveRoomForAmenities] = useState(null);

  // { "roomId:unitId": ["Wifi", "TV", ...] }
  const [amenitiesByUnit, setAmenitiesByUnit] = useState({});
  const [newAmenity, setNewAmenity] = useState("");
  const [savingAmenities, setSavingAmenities] = useState(false);
  const [amenitiesLoadingUnitKey, setAmenitiesLoadingUnitKey] = useState(null);

  const rooms = Array.isArray(user?.rooms) ? user.rooms : [];

  // ✅ FIX CRÍTICO: key único REAL por unidad
  const getRoomUnitKey = (room) =>
    `${Number(room.roomId)}:${Number(room.unitId)}`;

  // Normaliza imágenes
  const normalizeImages = (raw) => {
    if (!Array.isArray(raw)) return [];
    return raw.map((img) => {
      if (typeof img === "string") return { id: img, url: img };
      const url = img.url || img.path || img.src || "";
      return { ...img, url };
    });
  };

  // Normaliza amenities
  const normalizeAmenities = (raw) => {
    if (!raw) return [];
    if (Array.isArray(raw)) {
      return raw
        .map((a) => {
          if (typeof a === "string") return a.trim();
          if (a && typeof a === "object" && typeof a.name === "string")
            return a.name.trim();
          return null;
        })
        .filter((a) => a && a.length > 0);
    }
    if (typeof raw === "string") {
      return raw
        .split(",")
        .map((s) => s.trim())
        .filter((s) => s.length > 0);
    }
    return [];
  };

  // ✅ Wrappers para NO perder funcionalidad actual:
  // - Si tu backend ya soporta unitId: lo usa.
  // - Si no soporta, cae a la firma vieja (roomId) sin romper.
  const apiFetchAmenities = async (roomId, unitId) => {
    try {
      // si el api ya soporta (roomId, unitId)
      return await fetchRoomAmenities(roomId, unitId);
    } catch (e1) {
      // fallback firma vieja
      return await fetchRoomAmenities(roomId);
    }
  };

  const apiSaveAmenities = async (roomId, unitId, amenities) => {
    try {
      // si el api ya soporta (roomId, unitId, amenities)
      return await saveRoomAmenities(roomId, unitId, amenities);
    } catch (e1) {
      // fallback firma vieja (roomId, amenities)
      return await saveRoomAmenities(roomId, amenities);
    }
  };

  // ===== LOAD CURRENCY =====
  useEffect(() => {
    let alive = true;

    const loadCurrency = async () => {
      if (!user) return;
      try {
        const resp = await fetchBillingCurrency();
        if (!alive) return;
        if (resp?.success && resp.currency) setBillingCurrency(resp.currency);
      } catch (err) {
        console.error(
          "❌ Error al obtener moneda de facturación:",
          err.message,
        );
      }
    };

    loadCurrency();
    return () => {
      alive = false;
    };
  }, [user]);

  // ===== LOAD PRICES =====
  useEffect(() => {
    if (!user) return;
    if (rooms.length > 0) handleUpdatePrices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleUpdatePrices = async () => {
    if (!user) return;
    if (rooms.length === 0) return;

    setLoadingPrices(true);
    try {
      const roomIds = [...new Set(rooms.map((r) => r.roomId))];
      const body = { roomIds, startDate, endDate };
      const response = await fetchPrices(body);

      if (!response?.success || !response?.data?.success) {
        throw new Error(response?.message || "Error al obtener precios");
      }

      const calendarData = response.data.data;
      const priceMap = {};

      calendarData.forEach((roomEntry) => {
        const roomId = roomEntry.roomId;
        const calendar = roomEntry.calendar;
        if (calendar && calendar.length > 0) {
          const firstDay = calendar[0];
          priceMap[roomId] = firstDay.price1 ?? "—";
        }
      });

      setRoomPrices(priceMap);
    } catch (error) {
      console.error("❌ Error al actualizar precios:", error.message);
      alert("No se pudieron actualizar los precios.");
    } finally {
      setLoadingPrices(false);
    }
  };

  const handleSaveAll = async () => {
    if (!user) return;

    const updates = Object.entries(editedPrices).map(([roomId, price]) => ({
      roomId,
      price: Number(price),
    }));

    const payload = { prices: updates, startDate, endDate };

    try {
      await savePrices(payload);
      alert("✅ Precios guardados exitosamente");
      setEditedPrices({});
      await handleUpdatePrices();
    } catch (err) {
      console.error("❌ Error al guardar precios:", err.message);
      alert("Error al guardar precios");
    }
  };

  const handleBillingCurrencyChange = async (event) => {
    if (!user) return;

    const newCurrency = event.target.value;
    const prev = billingCurrency;

    setBillingCurrency(newCurrency);

    try {
      const resp = await saveBillingCurrency(newCurrency);
      if (!resp?.success)
        throw new Error(resp?.message || "Error al guardar moneda");
      alert("✅ Moneda de facturación actualizada.");
    } catch (err) {
      console.error("❌ Error al guardar moneda:", err.message);
      alert("Error al guardar la moneda de facturación.");
      setBillingCurrency(prev || user?.billing?.currency || "ARS");
    }
  };

  // ========== IMÁGENES (POR UNIDAD REAL) ==========
  const handleOpenImages = async (room) => {
    setActiveRoomForImages(room);
    setImageDialogOpen(true);

    const unitKey = getRoomUnitKey(room);

    try {
      setImagesLoadingUnitKey(unitKey);

      const resp = await fetchRoomImages(room.roomId, room.unitId);
      const images = normalizeImages(
        resp?.images || resp?.data?.images || resp?.data || [],
      );

      setImagesByUnit((prev) => ({ ...prev, [unitKey]: images }));
    } catch (err) {
      console.error("❌ Error al obtener imágenes:", err.message);
      alert("No se pudieron cargar las imágenes de esta habitación.");
    } finally {
      setImagesLoadingUnitKey(null);
    }
  };

  const handleCloseImages = () => {
    setImageDialogOpen(false);
    setActiveRoomForImages(null);
  };

  const handleUploadImage = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !activeRoomForImages) return;

    const unitKey = getRoomUnitKey(activeRoomForImages);

    try {
      const formData = new FormData();
      formData.append("image", file);

      await uploadRoomImage(
        activeRoomForImages.roomId,
        activeRoomForImages.unitId,
        formData,
      );

      const resp = await fetchRoomImages(
        activeRoomForImages.roomId,
        activeRoomForImages.unitId,
      );

      const images = normalizeImages(
        resp?.images || resp?.data?.images || resp?.data || [],
      );

      setImagesByUnit((prev) => ({ ...prev, [unitKey]: images }));
    } catch (err) {
      console.error("❌ Error al subir imagen:", err.message);
      alert("Error al subir la imagen.");
    } finally {
      event.target.value = "";
    }
  };

  const handleDeleteImage = async (image) => {
    if (!activeRoomForImages) return;

    const roomId = activeRoomForImages.roomId;
    const unitId = activeRoomForImages.unitId;
    const unitKey = getRoomUnitKey(activeRoomForImages);

    const imageId = image.id || image._id || image.url;
    if (!imageId) {
      console.warn("⚠️ No se encontró id de imagen para borrar:", image);
      return;
    }
    if (!window.confirm("¿Seguro que querés eliminar esta imagen?")) return;

    try {
      await deleteRoomImage(roomId, unitId, imageId);

      setImagesByUnit((prev) => ({
        ...prev,
        [unitKey]: (prev[unitKey] || []).filter(
          (img) =>
            (img.id || img._id || img.url) !==
            (image.id || image._id || image.url),
        ),
      }));
    } catch (err) {
      console.error("❌ Error al eliminar imagen:", err.message);
      alert("Error al eliminar la imagen.");
    }
  };

  // ========== AMENITIES (✅ POR UNIDAD, con fallback a roomId) ==========

  const handleOpenAmenities = async (room) => {
    setActiveRoomForAmenities(room);
    setAmenitiesDialogOpen(true);

    const unitKey = getRoomUnitKey(room);

    try {
      setAmenitiesLoadingUnitKey(unitKey);

      const resp = await apiFetchAmenities(room.roomId, room.unitId);

      // Soportar múltiples formas posibles del backend:
      // - { amenities: [...] }
      // - { data: { amenities: [...] } }
      // - { data: [...] }
      // - { amenitiesByUnit: { "roomId:unitId": [...] } } (si algún día lo devolvés así)
      const raw =
        resp?.amenitiesByUnit?.[unitKey] ??
        resp?.data?.amenitiesByUnit?.[unitKey] ??
        resp?.amenities ??
        resp?.data?.amenities ??
        resp?.data ??
        [];

      const amenities = normalizeAmenities(raw);

      setAmenitiesByUnit((prev) => ({ ...prev, [unitKey]: amenities }));
    } catch (err) {
      console.error("❌ Error al obtener amenities:", err.message);
      alert("No se pudieron cargar los amenities de esta habitación.");
    } finally {
      setAmenitiesLoadingUnitKey(null);
    }
  };

  const handleCloseAmenities = () => {
    setAmenitiesDialogOpen(false);
    setActiveRoomForAmenities(null);
    setNewAmenity("");
  };

  const handleAddAmenity = () => {
    if (!activeRoomForAmenities) return;
    const value = newAmenity.trim();
    if (!value) return;

    const unitKey = getRoomUnitKey(activeRoomForAmenities);

    setAmenitiesByUnit((prev) => {
      const current = prev[unitKey] || [];
      if (current.includes(value)) return prev;
      return { ...prev, [unitKey]: [...current, value] };
    });

    setNewAmenity("");
  };

  const handleRemoveAmenity = (amenity) => {
    if (!activeRoomForAmenities) return;
    const unitKey = getRoomUnitKey(activeRoomForAmenities);

    setAmenitiesByUnit((prev) => ({
      ...prev,
      [unitKey]: (prev[unitKey] || []).filter((a) => a !== amenity),
    }));
  };

  const handleSaveAmenities = async () => {
    if (!activeRoomForAmenities) return;

    const roomId = activeRoomForAmenities.roomId;
    const unitId = activeRoomForAmenities.unitId;
    const unitKey = getRoomUnitKey(activeRoomForAmenities);

    const amenities = amenitiesByUnit[unitKey] || [];

    setSavingAmenities(true);
    try {
      await apiSaveAmenities(roomId, unitId, amenities);
      alert("✅ Amenities guardados correctamente.");
      setAmenitiesDialogOpen(false);
      setActiveRoomForAmenities(null);
      setNewAmenity("");
    } catch (err) {
      console.error("❌ Error al guardar amenities:", err.message);
      alert("Error al guardar los amenities.");
    } finally {
      setSavingAmenities(false);
    }
  };

  // ✅ ahora sí
  if (!user) return null;

  const {
    username,
    email,
    role,
    plan,
    isBlocked,
    propertyId,
    linkUrl,
    employes,
    checkInHours,
    checkOutHours,
    whatsappLink,
    servicesDescription,
    billing,
  } = user;

  const activeUnitKey = activeRoomForImages
    ? getRoomUnitKey(activeRoomForImages)
    : null;
  const activeImages = activeUnitKey ? imagesByUnit[activeUnitKey] || [] : [];

  const activeAmenitiesUnitKey = activeRoomForAmenities
    ? getRoomUnitKey(activeRoomForAmenities)
    : null;
  const activeAmenities = activeAmenitiesUnitKey
    ? amenitiesByUnit[activeAmenitiesUnitKey] || []
    : [];

  return (
    <Box>
      <Paper sx={{ p: 3 }}>
        <Tabs
          value={tab}
          onChange={(_, newValue) => setTab(newValue)}
          textColor="primary"
          indicatorColor="primary"
          variant="scrollable"
        >
          <Tab label="Propiedad" />
          <Tab label="Habitaciones" />
          <Tab label="Facturación" />
          <Tab label="Cuenta" />
        </Tabs>

        <Divider sx={{ my: 2 }} />

        {/* 🏨 Propiedad */}
        <TabPanel value={tab} index={0}>
          <Typography variant="h6" gutterBottom>
            🏨 Información general
          </Typography>

          <Grid container spacing={2} mt={1}>
            <Info label="ID de Propiedad" value={propertyId} />
            <Info label="Link Web" value={linkUrl} />
            <Info label="Check-in" value={checkInHours} />
            <Info label="Check-out" value={checkOutHours} />
            <Info label="WhatsApp" value={whatsappLink} />
            <Info label="Servicios" value={servicesDescription} />
          </Grid>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" gutterBottom>
            👥 Empleados
          </Typography>
          {Array.isArray(employes) && employes.length > 0 ? (
            <Stack spacing={1}>
              {employes.map((emp, i) => (
                <Typography key={i}>• {emp}</Typography>
              ))}
            </Stack>
          ) : (
            <Typography variant="body2">No hay empleados cargados</Typography>
          )}
        </TabPanel>

        {/* 🛏️ Habitaciones */}
        <TabPanel value={tab} index={1}>
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            🛏️ Habitaciones configuradas
          </Typography>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            sx={{ mt: 2, mb: 2 }}
          >
            <TextField
              label="Desde"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Hasta"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <Button variant="contained" onClick={handleUpdatePrices}>
              Actualizar precios
            </Button>
          </Stack>

          {loadingPrices && (
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              ⏳ Cargando precios...
            </Typography>
          )}

          <TableContainer component={Paper} sx={{ mt: 2 }}>
            <Table size="small">
              <TableHead sx={{ backgroundColor: "#f0f0f0" }}>
                <TableRow>
                  <TableCell>
                    <strong>Nombre</strong>
                  </TableCell>
                  <TableCell>
                    <strong>ID</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Unidad</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Tipo</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Máx. Huéspedes</strong>
                  </TableCell>
                  <TableCell>
                    <Tooltip
                      title="Valor expresado en dólares estadounidenses"
                      arrow
                    >
                      <strong>$ Precio Base</strong>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <strong>Imágenes</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Amenities</strong>
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {rooms.map((room, index) => {
                  const isFirstOfGroup =
                    index === rooms.findIndex((r) => r.roomId === room.roomId);
                  const isGrouped =
                    rooms.filter((r) => r.roomId === room.roomId).length > 1;

                  return (
                    <TableRow
                      key={getRoomUnitKey(room)}
                      hover
                      sx={
                        isGrouped
                          ? {
                              borderLeft: "4px solid #1976d2",
                              backgroundColor: isFirstOfGroup
                                ? "#f9f9f9"
                                : "#fcfcfc",
                            }
                          : {}
                      }
                    >
                      <TableCell>{room.name}</TableCell>
                      <TableCell>{room.roomId}</TableCell>
                      <TableCell>{room.unitId}</TableCell>
                      <TableCell>
                        {room.type === "private" ? "Privada" : "Compartida"}
                      </TableCell>
                      <TableCell>{room.maxGuests}</TableCell>

                      {/* PRECIO: 1 por roomId */}
                      <TableCell>
                        {isFirstOfGroup ? (
                          <TextField
                            size="small"
                            type="number"
                            value={
                              editedPrices[room.roomId] ??
                              roomPrices[room.roomId] ??
                              ""
                            }
                            onChange={(e) =>
                              setEditedPrices((prev) => ({
                                ...prev,
                                [room.roomId]: e.target.value,
                              }))
                            }
                            sx={{ width: 90 }}
                          />
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            —
                          </Typography>
                        )}
                      </TableCell>

                      {/* ✅ IMÁGENES: botón por unidad */}
                      <TableCell>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<PhotoCameraIcon />}
                          onClick={() => handleOpenImages(room)}
                        >
                          Imágenes
                        </Button>
                      </TableCell>

                      {/* ✅ AMENITIES: AHORA botón por unidad (igual que imágenes) */}
                      <TableCell>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<PlaylistAddCheckIcon />}
                          onClick={() => handleOpenAmenities(room)}
                        >
                          Amenities
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {Object.keys(editedPrices).length > 0 && (
            <Box mt={2} textAlign="right">
              <Button
                variant="contained"
                color="success"
                onClick={handleSaveAll}
              >
                Guardar todos los precios
              </Button>
            </Box>
          )}

          {/* Modal imágenes */}
          <Dialog
            open={imageDialogOpen}
            onClose={handleCloseImages}
            fullWidth
            maxWidth="md"
          >
            <DialogTitle>
              Imágenes de{" "}
              {activeRoomForImages
                ? `${activeRoomForImages.name} (#${activeRoomForImages.roomId}) — Unidad ${activeRoomForImages.unitId}`
                : ""}
            </DialogTitle>

            <DialogContent dividers>
              {activeRoomForImages && (
                <>
                  <Button
                    variant="outlined"
                    startIcon={<PhotoCameraIcon />}
                    component="label"
                  >
                    Subir imagen
                    <input
                      type="file"
                      hidden
                      accept="image/*"
                      onChange={handleUploadImage}
                    />
                  </Button>

                  <Box mt={2}>
                    {imagesLoadingUnitKey === activeUnitKey ? (
                      <Typography variant="body2" color="textSecondary">
                        ⏳ Cargando imágenes...
                      </Typography>
                    ) : activeImages.length === 0 ? (
                      <Typography variant="body2" color="textSecondary">
                        No hay imágenes cargadas para esta habitación.
                      </Typography>
                    ) : (
                      <ImageList cols={3} gap={8}>
                        {activeImages.map((img) => {
                          const key = img._id || img.id || img.url;
                          const src = img.url || img.path || img.src || "";

                          return (
                            <ImageListItem key={key}>
                              <img
                                src={src}
                                alt={img.alt || activeRoomForImages.name}
                                loading="lazy"
                                style={{
                                  width: "100%",
                                  height: 180,
                                  objectFit: "cover",
                                  borderRadius: 4,
                                }}
                              />
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "flex-end",
                                  mt: 0.5,
                                }}
                              >
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleDeleteImage(img)}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            </ImageListItem>
                          );
                        })}
                      </ImageList>
                    )}
                  </Box>
                </>
              )}
            </DialogContent>

            <DialogActions>
              <Button onClick={handleCloseImages}>Cerrar</Button>
            </DialogActions>
          </Dialog>

          {/* Modal amenities */}
          <Dialog
            open={amenitiesDialogOpen}
            onClose={handleCloseAmenities}
            fullWidth
            maxWidth="sm"
          >
            <DialogTitle>
              Amenities de{" "}
              {activeRoomForAmenities
                ? `${activeRoomForAmenities.name} (#${activeRoomForAmenities.roomId}) — Unidad ${activeRoomForAmenities.unitId}`
                : ""}
            </DialogTitle>

            <DialogContent dividers>
              {activeRoomForAmenities && (
                <>
                  {amenitiesLoadingUnitKey === activeAmenitiesUnitKey ? (
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      sx={{ mb: 2 }}
                    >
                      ⏳ Cargando amenities...
                    </Typography>
                  ) : (
                    <>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        Agregá o quitá los amenities que se mostrarán para esta
                        unidad.
                      </Typography>

                      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                        <TextField
                          size="small"
                          label="Nuevo amenity"
                          value={newAmenity}
                          onChange={(e) => setNewAmenity(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              handleAddAmenity();
                            }
                          }}
                          sx={{ flex: 1 }}
                        />
                        <Button variant="contained" onClick={handleAddAmenity}>
                          Agregar
                        </Button>
                      </Stack>

                      <Box sx={{ minHeight: 60 }}>
                        {activeAmenities.length === 0 ? (
                          <Typography
                            variant="body2"
                            color="textSecondary"
                            sx={{ fontStyle: "italic" }}
                          >
                            Aún no hay amenities configurados.
                          </Typography>
                        ) : (
                          <Stack direction="row" spacing={1} flexWrap="wrap">
                            {activeAmenities.map((am) => (
                              <Chip
                                key={am}
                                label={am}
                                onDelete={() => handleRemoveAmenity(am)}
                                sx={{ mb: 1 }}
                              />
                            ))}
                          </Stack>
                        )}
                      </Box>
                    </>
                  )}
                </>
              )}
            </DialogContent>

            <DialogActions>
              <Button onClick={handleCloseAmenities} disabled={savingAmenities}>
                Cancelar
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSaveAmenities}
                disabled={savingAmenities}
              >
                {savingAmenities ? "Guardando..." : "Guardar amenities"}
              </Button>
            </DialogActions>
          </Dialog>
        </TabPanel>

        {/* 💸 Facturación */}
        <TabPanel value={tab} index={2}>
          <Typography variant="h6" gutterBottom>
            💸 Facturación
          </Typography>

          <Box sx={{ mt: 1, mb: 3 }}>
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel id="billing-currency-label">
                Moneda de facturación
              </InputLabel>
              <Select
                labelId="billing-currency-label"
                value={billingCurrency}
                label="Moneda de facturación"
                onChange={handleBillingCurrencyChange}
              >
                <MenuItem value="ARS">ARS – Peso argentino</MenuItem>
                <MenuItem value="CLP">CLP – Peso chileno</MenuItem>
              </Select>
            </FormControl>

            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Esta moneda se usará como referencia para los cargos del sistema y
              los montos mostrados en tu panel.
            </Typography>
          </Box>

          <Grid container spacing={2} mt={1}>
            <Info label="Saldo actual" value={`$${billing?.saldo}`} />
            <Info label="Costo diario" value={`$${billing?.costoDiario}`} />
            <Info
              label="Propiedades activas"
              value={billing?.dataRoom?.property}
            />
            <Info
              label="Habitaciones activas"
              value={billing?.dataRoom?.rooms}
            />
            <Info label="Canales conectados" value={billing?.dataRoom?.otas} />
            <Info label="Moneda actual" value={billingCurrency} />
          </Grid>
        </TabPanel>

        {/* 🧍 Cuenta */}
        <TabPanel value={tab} index={3}>
          <Typography variant="h6" gutterBottom>
            🧍 Cuenta
          </Typography>
          <Grid container spacing={2} mt={1}>
            <Info label="Usuario" value={username} />
            <Info label="Email" value={email} />
            <Info label="Rol" value={role} />
            <Info label="Plan" value={plan} />
            <Info label="¿Bloqueado?" value={isBlocked ? "Sí" : "No"} />
          </Grid>
        </TabPanel>
      </Paper>
    </Box>
  );
}
